import { useEffect, useRef, useState } from "react";
import { MdAddBox } from "react-icons/md";
import { FaRegSquare, FaMinusSquare } from "react-icons/fa";
import CmModal from "../Controls/CmModal";
import "../../style/scss/tree.scss";

function treeifyData(data) {
  data.forEach((cat) => {
    if (cat.Parent > 0) {
      const parent = data.find((f) => f.id === cat.Parent);
      if (parent) {
        cat.ParentNode = parent;
        if (!parent.children) parent.children = [];
        parent.children.push(cat);
      }
    }
  });
  return data;
}

function CategoryTreeNode({ value, openIDs, toggleNodeOpen, onChange, selectedID }) {
  const nodeId = value.id ?? value.ID;
  const hasChildren = (value.children || []).length > 0;
  const isOpen = (openIDs || []).indexOf(nodeId) >= 0;

  return (
    <div className={"TreeNode" + (nodeId === selectedID ? " selected" : "")}>
      <span className="NodeTitle">
        <span
          className="nodeIcons"
          onClick={(e) => {
            e?.stopPropagation();
            if (hasChildren) toggleNodeOpen(value);
          }}
        >
          {hasChildren ? (isOpen ? <FaMinusSquare /> : <MdAddBox />) : <FaRegSquare />}
        </span>
        <span
          className="nodeText"
          onClick={(e) => {
            e?.stopPropagation();
            onChange(value);
          }}
        >
          {value.Name}
        </span>
      </span>
      {hasChildren ? (
        <div className={"nodeChildren " + (isOpen ? "open" : "closed")}>
          {value.children.map((child) => {
            const childId = child.id ?? child.ID;
            return (
              <CategoryTreeNode
                key={childId}
                value={child}
                openIDs={openIDs}
                toggleNodeOpen={toggleNodeOpen}
                onChange={onChange}
                selectedID={selectedID}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// selectedId and onSelect({ id }) use numeric CategoryID.
function CategorySelectBody({ getData, selectedId, onSelect, onClose }) {
  const [rootNodes, setRoots] = useState([]);
  const [openIDs, setOpenIDs] = useState([]);
  const [loading, setLoading] = useState(typeof getData === "function");
  const [error, setError] = useState("");
  const loadGen = useRef(0);

  // Callers should pass a stable getData (useCallback) when possible.
  useEffect(() => {
    const gen = ++loadGen.current;
    let cancelled = false;

    async function load() {
      if (typeof getData !== "function") {
        setRoots([]);
        setError("");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await getData();
        if (cancelled || gen !== loadGen.current) return;
        const list = (res || []).map((n) => ({
          id: n.ID ?? n.id,
          ID: n.ID ?? n.id,
          Parent: n.Parent ?? n.parent ?? 0,
          Name: n.Name ?? n.name ?? "",
          children: [],
        }));
        list.sort((a, b) => ((a.Name || "") > (b.Name || "") ? 1 : -1));
        const data = treeifyData(list);
        const sel = Number(selectedId) || 0;
        const opened = [];
        if (sel) {
          let element = data.find((x) => x.id === sel);
          while (element) {
            const id = element.id ?? element.ID;
            if (element.children?.length && opened.indexOf(id) === -1) opened.push(id);
            element = element.ParentNode;
          }
        }
        setOpenIDs(opened);
        setRoots(data.filter((x) => !x.Parent));
      } catch (err) {
        if (cancelled || gen !== loadGen.current) return;
        console.error(err);
        setError("Could not load categories.");
        setRoots([]);
      } finally {
        if (!cancelled && gen === loadGen.current) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [getData, selectedId]);

  const toggleNodeOpen = (node) => {
    const id = node.id ?? node.ID;
    setOpenIDs((prev) => {
      const next = [...prev];
      const index = next.indexOf(id);
      if (index >= 0) next.splice(index, 1);
      else next.push(id);
      return next;
    });
  };

  const onNodeSelect = (node) => {
    const id = node.id ?? node.ID;
    onSelect?.({ id, name: node.Name || "" });
    onClose?.();
  };

  if (loading) {
    return <p className="category-select-status">Loading categories…</p>;
  }
  if (error) {
    return <p className="category-select-status">{error}</p>;
  }
  if (rootNodes.length === 0) {
    return <p className="category-select-status">No categories found.</p>;
  }

  const selectedID = Number(selectedId) || 0;

  return (
    <div className="Tree">
      {rootNodes.map((e) => (
        <CategoryTreeNode
          key={e.id}
          value={e}
          openIDs={openIDs}
          toggleNodeOpen={toggleNodeOpen}
          onChange={onNodeSelect}
          selectedID={selectedID}
        />
      ))}
    </div>
  );
}

export default function CategorySelectModal({
  open = false,
  onClose,
  onSelect,
  getData,
  selectedId,
  title = "Select category",
}) {
  return (
    <CmModal open={open} onClose={onClose} title={title} testId="category-select">
      <CategorySelectBody getData={getData} selectedId={selectedId} onSelect={onSelect} />
    </CmModal>
  );
}
